<script lang="ts">
import type vtkLPSView2DProxy from '@/src/vtk/LPSView2DProxy';

import vtkMouseRangeManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseRangeManipulator';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  toRefs,
  watch,
} from 'vue';

import { ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import useWindowingStore, {
  defaultWindowLevelConfig,
} from '@/src/store/view-configs/windowing';
import { useViewStore } from '@/src/store/views';

function computeStep(min: number, max: number) {
  return Math.min(max - min, 1) / 256;
}

export default defineComponent({
  name: 'WindowLevelTool',
  props: {
    viewId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { viewId: viewID } = toRefs(props);
    const windowingStore = useWindowingStore();
    const viewStore = useViewStore();
    const viewportStateStore = useViewportStateStore();

    const active = computed(
      () =>
        viewportStateStore.getToolMode(viewID.value)
        === ViewportToolMode.WindowLevel,
    );

    const currentWlID = computed(() =>
      viewportStateStore.getSelectedWlId(viewID.value),
    );

    const viewProxy = computed(() =>
      viewStore.getViewProxy<vtkLPSView2DProxy>(viewID.value),
    );

    const windowConfigDefaults = defaultWindowLevelConfig();
    const wlConfig = computed(() =>
      windowingStore.getConfig(viewID.value, currentWlID.value),
    );

    const wwRange = computed(() => ({
      min: 1e-12, // ensure we don't hit zero and jump to white
      max:
        wlConfig.value != null
          ? wlConfig.value.max - wlConfig.value.min
          : windowConfigDefaults.max,
      step:
        wlConfig.value != null
          ? computeStep(wlConfig.value.min, wlConfig.value.max)
          : computeStep(windowConfigDefaults.min, windowConfigDefaults.max),
    }));
    const wlRange = computed(() => ({
      min:
        wlConfig.value != null ? wlConfig.value.min : windowConfigDefaults.min,
      max:
        wlConfig.value != null ? wlConfig.value.max : windowConfigDefaults.max,
      step:
        wlConfig.value != null
          ? computeStep(wlConfig.value.min, wlConfig.value.max)
          : computeStep(windowConfigDefaults.min, windowConfigDefaults.max),
    }));

    const vertVal = computed(() =>
      wlConfig.value != null ? wlConfig.value.width : windowConfigDefaults.width,
    );

    const horizVal = computed(() =>
      wlConfig.value != null ? wlConfig.value.level : windowConfigDefaults.level,
    );

    // two manipulators to support with and without shiftkey
    const rangeManipulator = vtkMouseRangeManipulator.newInstance({
      button: 1,
      shift: false,
      scrollEnabled: false,
    });

    const shiftRangeManipulator = vtkMouseRangeManipulator.newInstance({
      button: 1,
      shift: true,
      scrollEnabled: false,
    });

    function detach(proxy: vtkLPSView2DProxy | null | undefined) {
      if (!proxy || proxy.isDeleted()) return;
      const istyle = proxy.getInteractorStyle2D();
      istyle.removeMouseManipulator(rangeManipulator);
      istyle.removeMouseManipulator(shiftRangeManipulator);
    }

    // attach manipulators only while the tool is active
    watch(
      [viewProxy, active] as const,
      ([curViewProxy, isActive], oldValues) => {
        const oldViewProxy = oldValues?.[0];
        if (oldViewProxy && oldViewProxy !== curViewProxy) {
          detach(oldViewProxy);
        }
        detach(curViewProxy);

        if (isActive && curViewProxy) {
          // assumed to be vtkInteractorStyleManipulator
          const istyle = curViewProxy.getInteractorStyle2D();
          istyle.addMouseManipulator(rangeManipulator);
          istyle.addMouseManipulator(shiftRangeManipulator);
        }
      },
      { immediate: true },
    );

    onBeforeUnmount(() => {
      detach(viewProxy.value);
    });

    function updateManipulator() {
      rangeManipulator.removeAllListeners();
      shiftRangeManipulator.removeAllListeners();
      const vertRange = wwRange.value;
      const horizRange = wlRange.value;

      [rangeManipulator, shiftRangeManipulator].forEach((manipulator) => {
        manipulator.setVerticalListener(
          vertRange.min,
          vertRange.max,
          vertRange.step,
          () => vertVal.value,
          (v) => {
            if (currentWlID.value != null) {
              windowingStore.updateConfig(viewID.value, currentWlID.value, {
                width: v,
              });
            }
          },
        );

        manipulator.setHorizontalListener(
          horizRange.min,
          horizRange.max,
          horizRange.step,
          () => horizVal.value,
          (v) => {
            if (currentWlID.value != null) {
              windowingStore.updateConfig(viewID.value, currentWlID.value, {
                level: v,
              });
            }
          },
        );
      });
    }

    watch(
      // Computed wwRange and wlRange are new objects with every mouse movement.
      // So only updateManipulator() if min/max/step change by comparing with deep equals.
      () => JSON.stringify({ w: wwRange.value, l: wlRange.value }),
      updateManipulator,
      { immediate: true },
    );

    return () => null;
  },
});
</script>
